import React, { useState, useEffect } from 'react'
import { Box, Typography, TextField, Dialog, DialogContent, IconButton, Button } from '@mui/material';
import { Close, Download, PictureAsPdf, VideoFile, InsertDriveFile, KeyboardArrowLeft, KeyboardArrowRight, ZoomIn, ZoomOut } from '@mui/icons-material';

const getFileType = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
  const pdfExts = ['pdf'];
  const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm'];

  if (imageExts.includes(ext)) return 'image';
  if (pdfExts.includes(ext)) return 'pdf';
  if (videoExts.includes(ext)) return 'video';
  return 'other';
};

const ImageGalleryDialog = ({ files, initialIndex, open, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    setSelectedIndex(initialIndex);
  }, [initialIndex, open]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') handlePrevious();
      if (event.key === 'ArrowRight') handleNext();
    };
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedIndex, open]);

  const handlePrevious = () => {
    setSelectedIndex(prev => prev > 0 ? prev - 1 : files.length - 1);
    setIsZoomed(false);
  };

  const handleNext = () => {
    setSelectedIndex(prev => prev < files.length - 1 ? prev + 1 : 0);
    setIsZoomed(false);
  };

  const currentFile = files[selectedIndex];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg"
      fullWidth
    >
      <DialogContent sx={{ p: 0, position: 'relative', backgroundColor: '#f5f5f5' }}>
        <IconButton
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            top: 8, 
            zIndex: 10,
            bgcolor: 'rgba(255,255,255,0.8)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
          }}
        >
          <Close />
        </IconButton>

        {/* Main Image */}
        <Box sx={{ 
          position: 'relative', 
          minHeight: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2
        }}>
          <Box sx={{ 
            position: 'relative', 
            maxWidth: '100%', 
            maxHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img
              src={currentFile.url}
              alt={currentFile.fileName}
              style={{
                maxWidth: '100%',
                maxHeight: '60vh',
                objectFit: 'contain',
                transform: isZoomed ? 'scale(1.5)' : 'scale(1)',
                transition: 'transform 300ms ease',
                cursor: isZoomed ? 'zoom-out' : 'zoom-in'
              }}
              onClick={() => setIsZoomed(!isZoomed)}
            />
          </Box>

          {/* Navigation Buttons */}
          {files.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevious}
                sx={{ 
                  position: 'absolute', 
                  left: 16, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  bgcolor: '#1976d2',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
              <IconButton
                onClick={handleNext}
                sx={{ 
                  position: 'absolute', 
                  right: 16, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  bgcolor: '#1976d2',
                  color: '#fff',
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                <KeyboardArrowRight />
              </IconButton>
            </>
          )}

          {/* Zoom Button */}
          <IconButton
            onClick={() => setIsZoomed(!isZoomed)}
            sx={{ 
              position: 'absolute', 
              top: 16, 
              right: 16,
              bgcolor: 'rgba(0,0,0,0.04)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
            }}
          >
            {isZoomed ? <ZoomOut /> : <ZoomIn />}
          </IconButton>

          {/* Image Counter */}
          <Box sx={{ 
            position: 'absolute', 
            bottom: 16, 
            right: 16,
            bgcolor: 'rgba(0,0,0,0.08)',
            borderRadius: '16px',
            px: 1.5,
            py: 0.5,
            fontSize: '0.8125rem',
            fontWeight: 500
          }}>
            {selectedIndex + 1} of {files.length}
          </Box>
        </Box>

        {/* Thumbnails */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          p: 2, 
          overflowX: 'auto',
          backgroundColor: '#fafafa',
          borderTop: '1px solid #eee'
        }}>
          {files.map((file, index) => (
            <Box
              key={index}
              onClick={() => { setSelectedIndex(index); setIsZoomed(false); }}
              sx={{
                width: '60px',
                height: '60px',
                flexShrink: 0,
                border: selectedIndex === index ? '3px solid #1976d2' : '3px solid transparent',
                borderRadius: '4px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 150ms ease',
                '&:hover': { transform: 'scale(1.05)' }
              }}
            >
              <img
                src={file.url}
                alt={file.fileName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const FilePreviewDialog = ({ file, open, onClose }) => {
  const fileType = getFileType(file?.fileName);

  const renderPreview = () => {
    switch (fileType) {
      case 'image':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', maxHeight: '70vh' }}>
            <img 
              src={file.url} 
              alt={file.fileName} 
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
            />
          </Box>
        );
      case 'pdf':
        return (
          <iframe
            src={file.url}
            title={file.fileName}
            style={{ width: '100%', height: '70vh', border: 'none' }}
          />
        );
      case 'video':
        return (
          <video
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '70vh' }}
          >
            <source src={file.url} />
            Your browser does not support the video tag.
          </video>
        );
      default:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <InsertDriveFile sx={{ fontSize: 80, color: '#757575' }} />
            <Typography variant="h6" sx={{ mt: 2 }}>Preview not available</Typography>
            <Button 
              variant="contained" 
              startIcon={<Download />}
              href={file.url}
              target="_blank"
              sx={{ mt: 2 }}
            >
              Download File
            </Button>
          </Box>
        );
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg"
      fullWidth
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            top: 8, 
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.8)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
          }}
        >
          <Close />
        </IconButton>
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Typography variant="subtitle1" fontWeight={600}>{file?.fileName}</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          {renderPreview()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const FolderItem = ({ folder, onDoubleClick }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(folder);

  const handleClick = (e) => {
    e.preventDefault();
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    setEditName(folder);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsEditing(false);
      setEditName(folder);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        padding: '8px',
        borderRadius: '4px',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#f5f5f5' }
      }}
      onClick={handleClick}
      onDoubleClick={onDoubleClick}
    >
      <img src={require('../../../assets/images/folder.png')} alt={folder} style={{ width: '64px', height: '64px', objectFit: 'contain' }}/>
      {isEditing ? (
        <TextField
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          size="small"
          sx={{ 
            mt: 0.5,
            '& .MuiInputBase-input': { 
              textAlign: 'center', 
              py: 0.5,
              px: 0.5,
              fontSize: '0.875rem'
            },
            width: '100%'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <Typography 
          variant='subtitle1' 
          align='center' 
          sx={{ 
            mt: 0.5,
            fontSize: '0.85rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%'
          }}
        >
          {folder}
        </Typography>
      )}
    </Box>
  );
};

const FileItem = ({ file, allFiles = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(file.fileName);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);

  const fileType = getFileType(file.fileName);
  const imageFiles = allFiles.filter(f => getFileType(f.fileName) === 'image');
  const isMultipleImages = imageFiles.length > 1;

  const handleFileClick = () => {
    if (fileType === 'image' && isMultipleImages) {
      setImageGalleryOpen(true);
    } else {
      setPreviewOpen(true);
    }
  };

  const handleNameClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    setEditName(file.fileName);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsEditing(false);
      setEditName(file.fileName);
    }
  };

  const renderPreview = () => {
    if (fileType === 'image') {
      return (
        <Box 
          sx={{ 
            width: '64px', 
            height: '64px', 
            overflow: 'hidden',
            borderRadius: '4px',
            backgroundColor: '#f5f5f5'
          }}
          onClick={handleFileClick}
        >
          <img 
            src={file.url} 
            alt={file.fileName}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover' 
            }} 
          />
        </Box>
      );
    }

    switch (fileType) {
      case 'pdf':
        return <PictureAsPdf sx={{ fontSize: 40, color: '#f44336', cursor: 'pointer' }} onClick={handleFileClick} />;
      case 'video':
        return <VideoFile sx={{ fontSize: 40, color: '#2196f3', cursor: 'pointer' }} onClick={handleFileClick} />;
      default:
        return <InsertDriveFile sx={{ fontSize: 40, color: '#757575', cursor: 'pointer' }} onClick={handleFileClick} />;
    }
  };

  return (
    <>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          padding: '8px',
          borderRadius: '4px',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#f5f5f5' }
        }}
        onClick={handleFileClick}
      >
        {renderPreview()}
        {isEditing ? (
          <TextField
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            size="small"
            sx={{ 
              mt: 0.5,
              '& .MuiInputBase-input': { 
                textAlign: 'center', 
                py: 0.5,
                px: 0.5,
                fontSize: '0.875rem'
              },
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <Typography 
            variant='subtitle1' 
            align='center' 
            sx={{ 
              mt: 0.5,
              fontSize: '0.85rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}
            onClick={handleNameClick}
            title="Click to rename"
          >
            {file.fileName}
          </Typography>
        )}
      </Box>
      
      {fileType === 'image' && isMultipleImages ? (
        <ImageGalleryDialog 
          files={imageFiles}
          initialIndex={imageFiles.findIndex(f => f.fileName === file.fileName)}
          open={imageGalleryOpen} 
          onClose={() => setImageGalleryOpen(false)} 
        />
      ) : (
        <FilePreviewDialog 
          file={file} 
          open={previewOpen} 
          onClose={() => setPreviewOpen(false)} 
        />
      )}
    </>
  );
};

export { FolderItem, FileItem };
